/**
 * A sonda do duplo toque — o painel que diz QUAL porta fechou, dentro do editor.
 *
 * ─── por que ela existe ─────────────────────────────────────────────────────────────────────
 *
 * A instrumentação autônoma do dashboard (`/diagnostico/toque`) mediu o WebKit do iPad e
 * derrubou as quatro hipóteses de uma vez: os dois `pointerdown` chegam limpos, dentro da
 * janela de 500 ms, **sem** `pointercancel`, **sem** `gesturestart`, e a contagem do
 * `ClickController` replicada chega a 2 nas quatro configurações testadas.
 *
 * Ou seja: o gesto existe, chega, e é contado. Ele se perde **depois** — entre o `doubleClick`
 * ser emitido e o editor de texto abrir. E esse trecho tem quatro portas que fecham em
 * SILÊNCIO, cada uma com um `return` mudo ou um `?.`:
 *
 * | | onde | o que fecha |
 * |---|---|---|
 * | 1 | `DefaultTool.doubleClick` | `if (this.doc.readonly)` — e aí o duplo clique dá **zoom** |
 * | 2 | `DefaultTool.doubleClick` | `this.interactivity?.` — sem interactivity, nada acontece |
 * | 3 | `InteractivityManager.dispatchEvent` | `if (activeInteraction$.peek()) return` |
 * | 4 | `GfxViewEventManager.dispatch` | `alvo?.dispatch(...)` — sem alvo, ninguém recebe |
 *
 * Nenhuma delas produz erro, log ou sintoma. Perguntar "qual delas?" lendo o código não
 * responde: as quatro são plausíveis, e três dependem de estado de runtime que só existe no
 * aparelho. Daí a sonda.
 *
 * ─── por que este arquivo não importa nada ──────────────────────────────────────────────────
 *
 * Mesmo motivo do `duplo-toque.ts` e do `decidir-pan.ts`: sem `import`, ele roda nos testes
 * desta estação sem `node_modules` do Affine e sem os ~16 minutos de build que a Regra 8.7.3
 * cataloga. O que vai nos arquivos do upstream é **uma chamada por ponto** — a Regra 8.15 na
 * prática: arquivo novo não conflita, arquivo editado conflita.
 *
 * ⚠️ O desenho na tela precisa de `document`, e ele entra **por parâmetro**. Não é preciosismo:
 * é o que permite `cli/tests/js/sonda-do-toque.test.ts` exercitar o painel inteiro com um
 * dublê, sem navegador.
 *
 * ─── e por que ela é opt-in por ENDEREÇO ────────────────────────────────────────────────────
 *
 * `?tuninho-toque=1`. Desligada, `registrar()` sai na primeira linha e o custo é uma
 * comparação de string por evento — nada é alocado, nada é desenhado, nada é medido. Um painel
 * de diagnóstico que aparecesse sozinho seria pior que não ter painel.
 */

/** Um ponto do caminho, e o que ele respondeu quando o gesto passou por ali. */
export interface Marco {
  /** O nome curto da porta, como ela aparece no painel. */
  nome: string;
  /** `true` = o gesto passou; `false` = esta porta fechou; `null` = nem chegou aqui. */
  passou: boolean | null;
  /** O que foi observado, em uma linha. É isto que nomeia a causa. */
  detalhe: string;
}

/** A ordem é a do caminho percorrido, e o painel a preserva: a primeira `false` é a culpada. */
export const PORTAS = [
  'ClickController emitiu doubleClick',
  'DefaultTool deixou passar (nao e readonly)',
  'InteractivityManager deixou passar',
  'GfxViewEventManager achou alvo',
] as const;

export type Porta = (typeof PORTAS)[number];

/**
 * A sonda está ligada para este endereço?
 *
 * Recebe a *query string* em vez de ler `location` sozinha — é o que torna a decisão testável
 * sem navegador, e é a mesma escolha que o resto dos módulos puros daqui já faz.
 */
export function sondaLigada(busca: string | undefined): boolean {
  if (!busca) return false;
  // Sem `URLSearchParams`: ele existe no navegador e no node, mas manter o arquivo sem NENHUMA
  // dependência externa é o que garante que ele carrega em qualquer runner.
  const limpa = busca.charAt(0) === '?' ? busca.slice(1) : busca;
  return limpa
    .split('&')
    .some(par => par === 'tuninho-toque=1' || par === 'tuninho-toque');
}

/**
 * A porta culpada é a PRIMEIRA que fechou — as seguintes nem chegaram a ser perguntadas.
 *
 * Devolver a última, ou "todas as que falharam", mandaria quem lê o painel investigar efeito em
 * vez de causa. Devolve `null` quando o gesto atravessou tudo: aí o defeito está adiante do que
 * esta sonda cobre, e dizer isso é mais honesto que apontar a última porta por eliminação.
 */
export function primeiraQueFechou(marcos: Marco[]): Marco | null {
  for (const m of marcos) {
    if (m.passou === false) return m;
  }
  return null;
}

/**
 * O veredito em uma frase — é o que a pessoa lê no print antes de qualquer outra coisa.
 */
export function veredito(marcos: Marco[]): string {
  if (!marcos.length) return 'nenhum duplo toque medido ainda';

  const culpada = primeiraQueFechou(marcos);
  if (culpada) return `FECHOU EM: ${culpada.nome} — ${culpada.detalhe}`;

  const naoChegou = marcos.find(m => m.passou === null);
  if (naoChegou) return `NÃO CHEGOU A: ${naoChegou.nome}`;

  return 'PASSOU POR TUDO — o defeito está adiante desta sonda';
}

/** O corpo do painel: uma linha por porta, na ordem do caminho. */
export function linhas(marcos: Marco[]): string[] {
  return marcos.map(m => {
    const marca = m.passou === true ? '✓' : m.passou === false ? '✗' : '·';
    return `${marca} ${m.nome}${m.detalhe ? `  — ${m.detalhe}` : ''}`;
  });
}

/**
 * O acumulador de uma passada.
 *
 * Ele guarda os marcos de UM gesto. `comecar()` zera — e zera MUTANDO a lista, nunca
 * substituindo o objeto: quem já tem a referência (os pontos de fiação, capturados em closure)
 * continuaria escrevendo na lista antiga, e o painel mostraria de volta os números da passada
 * anterior. Esse defeito exato já apareceu na instrumentação autônoma deste mesmo card e
 * custou uma rodada — ele fica registrado aqui para não voltar pela terceira porta.
 */
export class SondaDoToque {
  readonly marcos: Marco[] = [];

  comecar(): void {
    this.marcos.length = 0;
  }

  marcar(nome: string, passou: boolean | null, detalhe = ''): void {
    this.marcos.push({ nome, passou, detalhe });
  }

  texto(): string {
    return [veredito(this.marcos), '', ...linhas(this.marcos)].join('\n');
  }
}

/** O mínimo de `document` que o painel usa — declarado para o dublê do teste ser honesto. */
export interface DocumentoMinimo {
  getElementById(id: string): { textContent: string | null } | null;
  createElement(tag: string): {
    id: string;
    style: { cssText: string };
    textContent: string | null;
  };
  body: { appendChild(no: unknown): void } | null;
}

export const ID_DO_PAINEL = 'tuninho-sonda-do-toque';

const ESTILO = [
  'position:fixed', 'left:8px', 'bottom:8px', 'z-index:2147483647',
  'max-width:min(92vw,560px)', 'padding:10px 12px', 'border-radius:10px',
  'background:rgba(8,10,14,.94)', 'color:#e6e8ee', 'border:1px solid #2a2f3a',
  'font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace',
  'white-space:pre-wrap', 'pointer-events:none',
].join(';');

/**
 * Desenha (ou atualiza) o painel.
 *
 * ⚠️ `pointer-events: none` no estilo: o painel fica POR CIMA do quadro e não pode roubar um
 * toque do gesto que ele existe para medir. Um instrumento que interfere no que mede responde
 * sobre um ambiente que não existe — é a mesma decisão que fez a instrumentação autônoma deste
 * card não ter um único `preventDefault`.
 */
export function desenharPainel(doc: DocumentoMinimo, texto: string): void {
  let no = doc.getElementById(ID_DO_PAINEL);
  if (!no) {
    const criado = doc.createElement('div');
    criado.id = ID_DO_PAINEL;
    criado.style.cssText = ESTILO;
    if (!doc.body) return;
    doc.body.appendChild(criado);
    no = criado;
  }
  no.textContent = texto;
}

/**
 * A instância única, e por que ela mora no `globalThis`.
 *
 * Os três pontos de fiação vivem em arquivos diferentes do mesmo pacote. Um módulo com estado
 * de topo resolveria — mas o bundler pode duplicar o módulo entre *chunks*, e duas sondas
 * fariam o painel mostrar metade do caminho sem nada indicando isso. O `globalThis` é uma
 * instância só por página, por construção.
 *
 * ⚠️ E ela só NASCE se a sonda estiver ligada. Desligada, isto devolve `null`, os pontos de
 * fiação saem na primeira linha, e nada é alocado nem medido.
 */
const CHAVE = '__tuninhoSondaDoToque';

interface Hospedeiro {
  [CHAVE]?: SondaDoToque | null;
  location?: { search?: string };
}

export function obterSonda(hospedeiro: Hospedeiro = globalThis as Hospedeiro): SondaDoToque | null {
  const existente = hospedeiro[CHAVE];
  if (existente !== undefined) return existente;

  const ligada = sondaLigada(hospedeiro.location?.search);
  const nova = ligada ? new SondaDoToque() : null;
  hospedeiro[CHAVE] = nova;
  return nova;
}

/**
 * O que os pontos de fiação chamam. Uma linha em cada, e ela não lança nunca:
 * instrumentação que derruba o que instrumenta é pior que instrumentação nenhuma.
 */
export function marcar(nome: string, passou: boolean | null, detalhe = ''): void {
  try {
    obterSonda()?.marcar(nome, passou, detalhe);
  } catch {
    /* uma sonda não pode quebrar o editor */
  }
}

/** Começa uma passada nova e apaga a anterior — chamado no `pointerdown` que abre o gesto. */
export function comecarPassada(): void {
  try {
    obterSonda()?.comecar();
  } catch {
    /* idem */
  }
}

/** Redesenha o painel com o estado atual. Sem `document` (SSR, teste), não faz nada. */
export function pintar(doc?: DocumentoMinimo): void {
  try {
    const s = obterSonda();
    if (!s) return;
    const alvo = doc ?? (globalThis as { document?: DocumentoMinimo }).document;
    if (!alvo) return;
    desenharPainel(alvo, s.texto());
  } catch {
    /* idem */
  }
}

/**
 * A sonda está ligada? — para quem precisa decidir ANTES de pagar por medir.
 *
 * O `marcar` já sai barato quando desligada, mas há ponto de fiação em caminho quente onde o
 * caro é o **argumento**, não a chamada: no `GfxViewEventManager`, montar o detalhe exige uma
 * busca no grid. Perguntar primeiro é o que mantém o custo em zero para quem não ligou nada.
 */
export function ativa(): boolean {
  try {
    return obterSonda() !== null;
  } catch {
    return false;
  }
}
